import React, { lazy, Suspense } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { GenericPageShimmer } from '../../loader/components/generic-page-shimmer/GenericPageShimmer';
import { LazyActivation } from '../../containers/activate/LazyActivation';
import styles from './Dashboard.module.scss';

const routers = {
  Account: lazy(() => import('../../account/components/account/Account')),

  Target: lazy(() => import('../../containers/activate-target').then(module => ({ default: module.Target }))),

  Destination: lazy(() =>
    import('../../containers/destination/destinations-container/components/Destinations').then(module => ({ default: module.Destination }))
  ),

  EnterpriseDestination: lazy(() =>
    import('../../edge/destinations/destinations/Destinations').then(module => ({
      default: module.Destinations
    }))
  ),

  EnterprisePipeline: lazy(() =>
    import('../../edge/pipelines/Pipelines').then(module => ({
      default: module.Pipelines
    }))
  ),

  JobMonitoringDashboard: lazy(() =>
    import('../../containers/job-monitoring/components/job-monitoring-dashboard/JobMonitoringDashboard').then(module => ({
      default: module.JobMonitoringDashboard
    }))
  ),

  NotificationSettings: lazy(() =>
    import('../../containers/notification-settings/components/notification-settings/NotificationSettings').then(module => ({
      default: module.NotificationSettings
    }))
  ),

  Payment: lazy(() => import('../../containers/payment').then(module => ({ default: module.Payment }))),

  SetupBilling: lazy(() =>
    import('../../containers/payment/SetupBilling').then(module => ({
      default: module.SetupBilling
    }))
  ),

  Pipeline: lazy(() => import('../../containers/pipeline/components/pipeline/Pipeline').then(module => ({ default: module.Pipeline }))),

  Team: lazy(() => import('../../containers/team').then(module => ({ default: module.Team }))),

  TransformModel: lazy(() =>
    import('../../containers/transform-model').then(module => ({ default: module.TransformModel }))
  ),

  Workflow: lazy(() => import('../../containers/workflow/components/workflow/Workflow').then(module => ({ default: module.Workflow }))),

  AccountSuspended: lazy(() =>
    import('../../containers/team/AccountSuspended').then(module => ({
      default: module.AccountSuspended
    }))
  ),

  DownloadPage: lazy(() =>
    import('../../download-page/components/DownloadPage').then(module => ({ default: module.DownloadPage }))
  ),

  EmailVerificationBlock: lazy(() =>
    import('../../auth/components/email-verification-block/EmailVerificationBlock').then(module => ({
      default: module.EmailVerificationBlock
    }))
  ),

  TrailExpired: lazy(() =>
    import('../../containers/team/TrialExpired/TrialExpiredPage').then(module => ({
      default: module.TrialExpired
    }))
  )
};

function LazyComponent({ component }) {
  const Component = routers[component];
  return (
    <Suspense fallback={<GenericPageShimmer />}>
      <Component />
    </Suspense>
  );
}

export function DashboardRoutes() {
  return (
    <div className={styles.dashboardOutletContainer}>
      <Routes>
        <Route
          key='destination-edge'
          path='/destination/edge/*'
          element={<LazyComponent component='EnterpriseDestination' />}
        />
        <Route
          key='pipeline-edge'
          path='/pipeline/edge/*'
          element={<LazyComponent component='EnterprisePipeline' />}
        />
        <Route
          key='destination'
          path='/destination/*'
          element={<LazyComponent component='Destination' />}
        />
        <Route key='workflow' path='/workflow/*' element={<LazyComponent component='Workflow' />} />
        <Route key='pipeline' path='/pipeline/*' element={<LazyComponent component='Pipeline' />} />
        <Route key='model' path='/model/*' element={<LazyComponent component='TransformModel' />} />
        <Route
          key='activation'
          path='/activation/*'
          element={
            <Suspense fallback={<GenericPageShimmer />}>
              <LazyActivation />
            </Suspense>
          }
        />
        <Route key='target' path='/target/*' element={<LazyComponent component='Target' />} />
        <Route key='account' path='/account/*' element={<LazyComponent component='Account' />} />
        <Route key='team' path='/team/*' element={<LazyComponent component='Team' />} />
        <Route
          key='payment-setup-billing'
          path='/payment/setup-billing'
          element={<LazyComponent component='SetupBilling' />}
        />
        <Route key='payment' path='/payment/*' element={<LazyComponent component='Payment' />} />
        <Route
          key='alerts'
          path='/alerts/*'
          element={<LazyComponent component='NotificationSettings' />}
        />
        <Route
          key='trial-expired'
          path='/trial-expired'
          element={<LazyComponent component='TrailExpired' />}
        />
        <Route
          key='email-verification'
          path='/verify-email-block'
          element={<LazyComponent component='EmailVerificationBlock' />}
        />
        <Route
          key='download-page'
          path='/download/:feature/:filename'
          element={<LazyComponent component='DownloadPage' />}
        />
        <Route
          key='account-suspended'
          path='/account-suspended'
          element={<LazyComponent component='AccountSuspended' />}
        />
        <Route
          key='all-jobs'
          path='/all-jobs'
          element={<LazyComponent component='JobMonitoringDashboard' />}
        />
        <Route path='*' element={<Navigate to='/pipeline' replace />} />
      </Routes>
    </div>
  );
}
